import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import type {
  ResourceModelConfig,
  EventModelConfig,
} from '@bryntum/scheduler-thin';
import { parseISO } from 'date-fns';

import {
  useSchedulersWithTargetsQuery,
  useColleaguesCalendarShiftsDataQuery,
} from '../../../__generated__/graphql';
import {
  type ViewType,
  VIEW_PARAM,
  DATE_PARAM,
  SCHEDULER_PARAM,
} from './types';
import { format } from 'date-fns';
import { getDateRangeForView, parsePageQueryParams } from './utils';
import { useToasts } from '../../../../../context/Toast';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { mapOpenShiftsToEvents } from '../components/mapOpenShiftsToEvents';

export const OPEN_SHIFTS_RESOURCE_ID = 'open-shifts-row';

export type SchedulerOption = {
  Id: string;
  Name: string;
};

export type TargetOption = {
  id: string;
  name: string;
};

export function useColleaguesCalendarData(showOpenShifts: boolean) {
  const history = useHistory();
  const { addError } = useToasts();
  const t = useNamespacedTranslation('schedulerShifts.colleaguesCalendar');

  const initialParams = useRef(
    parsePageQueryParams(new URLSearchParams(history.location.search)),
  ).current;

  const [view, setView] = useState<ViewType>(initialParams.view);
  const [currentDate, setCurrentDate] = useState<Date>(initialParams.date);
  const [selectedScheduler, setSelectedScheduler] =
    useState<SchedulerOption | null>(null);
  const [selectedTargetIds, setSelectedTargetIds] = useState<string[] | null>(
    null,
  );

  const {
    data: schedulersWithTargetsData,
    loading: schedulersLoading,
    error: schedulersError,
  } = useSchedulersWithTargetsQuery();

  const schedulers: SchedulerOption[] = useMemo(
    () =>
      (schedulersWithTargetsData?.me?.schedulersWithTargets ?? []).map((s) => ({
        Id: s.Id,
        Name: s.Name,
      })),
    [schedulersWithTargetsData?.me?.schedulersWithTargets],
  );

  const effectiveScheduler = useMemo(() => {
    if (
      selectedScheduler &&
      schedulers.some((s) => s.Id === selectedScheduler.Id)
    )
      return selectedScheduler;
    if (initialParams.schedulerId) {
      const fromUrl = schedulers.find(
        (s) => s.Id === initialParams.schedulerId,
      );
      if (fromUrl) return fromUrl;
    }
    return schedulers[0] ?? null;
  }, [selectedScheduler, initialParams.schedulerId, schedulers]);

  const selectedSchedulerData = useMemo(
    () =>
      (schedulersWithTargetsData?.me?.schedulersWithTargets ?? []).find(
        (s) => s.Id === effectiveScheduler?.Id,
      ),
    [
      schedulersWithTargetsData?.me?.schedulersWithTargets,
      effectiveScheduler?.Id,
    ],
  );

  const allTargets: TargetOption[] = useMemo(
    () =>
      (selectedSchedulerData?.allTargets ?? []).map((target) => ({
        id: target.id,
        name: target.name,
      })),
    [selectedSchedulerData?.allTargets],
  );

  const employeeTargetIds: string[] = useMemo(
    () =>
      (selectedSchedulerData?.employeeTargets ?? []).map((target) => target.id),
    [selectedSchedulerData?.employeeTargets],
  );

  // Derive effective target IDs: user selection takes priority, otherwise default to employee targets
  const effectiveTargetIds = selectedTargetIds ?? employeeTargetIds;

  const { startDate, endDate } = useMemo(
    () => getDateRangeForView(currentDate, view),
    [currentDate, view],
  );

  const {
    data: shiftsData,
    loading: shiftsLoading,
    error: shiftsError,
  } = useColleaguesCalendarShiftsDataQuery({
    variables: {
      input: {
        schedulerId: effectiveScheduler?.Id ?? '',
        targetIds: effectiveTargetIds,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        // Colleagues Calendar intentionally shows every colleague's shift.
        onlyMyShifts: null,
      },
    },
    skip:
      !effectiveScheduler?.Id ||
      effectiveTargetIds.length === 0 ||
      !startDate ||
      !endDate,
  });

  useEffect(() => {
    if (schedulersError || shiftsError) {
      addError(t('errorLoadingData'));
    }
  }, [schedulersError, shiftsError, addError, t]);

  const meId = shiftsData?.me?.Id ?? '';

  // Build intervals of the current employee's shifts for overlap checking
  const myShiftIntervals = useMemo(() => {
    if (!shiftsData?.me?.schedulerShiftsData?.shifts || !meId) return [];
    return shiftsData.me.schedulerShiftsData.shifts
      .filter((s) => s.flair__Resource_Employee__c === meId)
      .map((s) => ({
        start: parseISO(s.flair__Start_Datetime__c),
        end: parseISO(s.flair__End_Datetime__c),
      }));
  }, [shiftsData?.me?.schedulerShiftsData?.shifts, meId]);

  const openShifts = useMemo(
    () => shiftsData?.me?.schedulerShiftsData?.openShifts ?? [],
    [shiftsData?.me?.schedulerShiftsData?.openShifts],
  );

  const resources: ResourceModelConfig[] = useMemo(() => {
    if (!shiftsData?.me?.schedulerShiftsData?.employees) return [];

    const employeeResources = shiftsData.me.schedulerShiftsData.employees.map(
      (employee) => ({
        id: employee.Id,
        name:
          employee.flair__First_Name__c && employee.flair__Last_Name__c
            ? `${employee.flair__First_Name__c} ${employee.flair__Last_Name__c}`
            : employee.Name,
        avatarUrl: employee.avatar?.url ?? null,
      }),
    );

    if (showOpenShifts && openShifts.length > 0) {
      return [
        {
          id: OPEN_SHIFTS_RESOURCE_ID,
          name: t('openShifts'),
          isOpenShiftRow: true,
        },
        ...employeeResources,
      ];
    }

    return employeeResources;
  }, [
    shiftsData?.me?.schedulerShiftsData?.employees,
    openShifts.length,
    t,
    showOpenShifts,
  ]);

  const targetNameMap: Record<string, string> = useMemo(
    () =>
      Object.fromEntries(allTargets.map((target) => [target.id, target.name])),
    [allTargets],
  );

  const { events, openShiftsCount } = useMemo(() => {
    if (!shiftsData?.me?.schedulerShiftsData?.shifts)
      return { events: [] as EventModelConfig[], openShiftsCount: 0 };

    const shiftEvents = shiftsData.me.schedulerShiftsData.shifts.map(
      (shift) => ({
        id: shift.Id,
        name: shift.flair__Title__c || '',
        startDate: new Date(shift.flair__Start_Datetime__c),
        endDate: new Date(shift.flair__End_Datetime__c),
        resourceId: shift.flair__Resource_Employee__c ?? '',
        eventColor: shift.flair__Shift_Color__c,
        eventStyle: 'border' as const,
        targetName: shift.flair__Linked_Target_Id__c
          ? targetNameMap[shift.flair__Linked_Target_Id__c] ?? null
          : null,
      }),
    );

    const openShiftEvents = mapOpenShiftsToEvents({
      openShifts,
      meId,
      myShiftIntervals,
      targetNameMap,
      openShiftLabel: t('openShift'),
    }).map((event) => ({
      ...event,
      resourceId: OPEN_SHIFTS_RESOURCE_ID,
      eventStyle: 'colored' as const,
    }));

    return {
      events: (showOpenShifts
        ? [...shiftEvents, ...openShiftEvents]
        : shiftEvents) as EventModelConfig[],
      openShiftsCount: openShiftEvents.length,
    };
  }, [
    shiftsData?.me?.schedulerShiftsData?.shifts,
    targetNameMap,
    openShifts,
    meId,
    myShiftIntervals,
    t,
    showOpenShifts,
  ]);

  const initialLoading = schedulersLoading;
  const dataLoading = shiftsLoading;

  const updateUrl = useCallback(
    (newView: ViewType, newDate: Date, newSchedulerId?: string | null) => {
      const params = new URLSearchParams(history.location.search);
      const newDateStr = format(newDate, 'yyyy-MM-dd');
      const currentViewParam = params.get(VIEW_PARAM);
      const currentDateParam = params.get(DATE_PARAM);
      const currentScheduler = params.get(SCHEDULER_PARAM);
      const schedulerId =
        newSchedulerId !== undefined
          ? newSchedulerId
          : effectiveScheduler?.Id ?? currentScheduler;
      if (
        currentViewParam === newView &&
        currentDateParam === newDateStr &&
        currentScheduler === (schedulerId ?? '')
      ) {
        return;
      }
      params.set(VIEW_PARAM, newView);
      params.set(DATE_PARAM, newDateStr);
      if (schedulerId) {
        params.set(SCHEDULER_PARAM, schedulerId);
      } else {
        params.delete(SCHEDULER_PARAM);
      }
      history.replace({ search: params.toString() });
    },
    [history, effectiveScheduler?.Id],
  );

  const handleSchedulerChange = useCallback(
    (scheduler: SchedulerOption | null) => {
      setSelectedScheduler(scheduler);
      setSelectedTargetIds(null);
      updateUrl(view, currentDate, scheduler?.Id ?? null);
    },
    [view, currentDate, updateUrl],
  );

  const handleTargetsChange = useCallback((targetIds: string[]) => {
    setSelectedTargetIds(targetIds);
  }, []);

  const handleViewChange = useCallback(
    (newView: ViewType) => {
      setView(newView);
      updateUrl(newView, currentDate);
    },
    [currentDate, updateUrl],
  );

  const handleDateChange = useCallback(
    (newDate: Date) => {
      setCurrentDate(newDate);
      updateUrl(view, newDate);
    },
    [view, updateUrl],
  );

  return {
    view,
    currentDate,
    startDate,
    endDate,
    schedulers,
    effectiveScheduler,
    allTargets,
    effectiveTargetIds,
    resources,
    events,
    openShiftsCount,
    initialLoading,
    dataLoading,
    handleSchedulerChange,
    handleTargetsChange,
    handleViewChange,
    handleDateChange,
  };
}
